import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { CheckCircle2, Inbox, Loader2, Lock, Send, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Appeal,
  AppealRoom,
  AppealSession,
  VERDICTS_TO_DECIDE,
  submitVerdict,
} from '../../../../services/appealsService';
import {
  AppealRequest,
  ClosedNotice,
  EmptyState,
  ErrorText,
  QuestionView,
  RichTextView,
  RoomHeader,
  Tabs,
  cardCls,
  eyebrowCls,
  fieldCls,
  formatTime,
  primaryBtnCls,
  secondaryBtnCls,
} from './shared';

type Filter = 'todo' | 'all';

/** A verdict is final, so the juror confirms it in a dialog before it is sent. */
const VerdictForm: React.FC<{ session: AppealSession; appeal: Appeal; label: string }> = ({ session, appeal, label }) => {
  const [accepted, setAccepted] = useState<boolean | null>(null);
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);

  const review = (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return setError('Rəyin əsaslandırmasını yazın.');
    if (accepted === null) return setError('Bəli və ya xeyr seçin.');
    setError('');
    setConfirming(true);
  };

  const send = async () => {
    if (accepted === null) return;
    setSending(true);
    try {
      await submitVerdict(session, appeal.id, accepted, text);
      toast.success('Rəy göndərildi');
      setConfirming(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Rəy göndərilmədi.');
      setConfirming(false);
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={review} className="mt-4 space-y-3 border-t border-gray-100 dark:border-gray-800 pt-4">
      <p className={eyebrowCls}>Sizin rəyiniz</p>
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => setAccepted(true)}
          aria-pressed={accepted === true}
          className={cn(
            'inline-flex items-center justify-center gap-2 rounded-xl border-2 px-4 py-2.5 text-sm font-bold transition-colors',
            accepted === true
              ? 'border-green-600 bg-green-600 text-white'
              : 'border-gray-200 dark:border-gray-700 text-green-700 dark:text-green-300 hover:bg-green-50 dark:hover:bg-green-950'
          )}
        >
          <CheckCircle2 className="w-5 h-5" />
          Bəli, qəbul
        </button>
        <button
          type="button"
          onClick={() => setAccepted(false)}
          aria-pressed={accepted === false}
          className={cn(
            'inline-flex items-center justify-center gap-2 rounded-xl border-2 px-4 py-2.5 text-sm font-bold transition-colors',
            accepted === false
              ? 'border-red-600 bg-red-600 text-white'
              : 'border-gray-200 dark:border-gray-700 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-950'
          )}
        >
          <XCircle className="w-5 h-5" />
          Xeyr, rədd
        </button>
      </div>
      <textarea
        rows={4}
        value={text}
        onChange={e => setText(e.target.value)}
        maxLength={4000}
        placeholder="Rəyiniz və əsaslandırması…"
        aria-label="Rəy"
        className={fieldCls}
      />
      {error && <ErrorText>{error}</ErrorText>}
      <div className="flex justify-end">
        <button type="submit" className={primaryBtnCls}>
          <Send className="w-4 h-4" />
          Rəyi göndər
        </button>
      </div>

      <Dialog open={confirming} onOpenChange={open => !sending && setConfirming(open)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rəyi təsdiqləyin</DialogTitle>
            <DialogDescription>
              {label}. Göndərildikdən sonra rəyi dəyişmək mümkün olmayacaq.
            </DialogDescription>
          </DialogHeader>
          <div
            className={cn(
              'rounded-xl px-3 py-2.5 text-sm',
              accepted
                ? 'bg-green-50 text-green-800 dark:bg-green-950 dark:text-green-200'
                : 'bg-red-50 text-red-800 dark:bg-red-950 dark:text-red-200'
            )}
          >
            <p className="inline-flex items-center gap-1.5 font-bold">
              {accepted ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
              {accepted ? 'Bəli, qəbul' : 'Xeyr, rədd'}
            </p>
            <p className="mt-1 max-h-48 overflow-y-auto whitespace-pre-line">{text.trim()}</p>
          </div>
          <DialogFooter className="gap-2">
            <button type="button" onClick={() => setConfirming(false)} disabled={sending} className={secondaryBtnCls}>
              Geri qayıt
            </button>
            <button type="button" onClick={send} disabled={sending} className={primaryBtnCls}>
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Təsdiqlə və göndər
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </form>
  );
};

const AppealItem: React.FC<{ room: AppealRoom; session: AppealSession; appeal: Appeal; label: string }> = ({
  room,
  session,
  appeal,
  label,
}) => {
  // The server sends only this juror's own verdict, never anyone else's
  const mine = appeal.verdicts[0];

  return (
    <div className={cn(cardCls, 'p-4 sm:p-5')}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-black text-gray-900 dark:text-gray-50">{label}</span>
        <span className="inline-flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
          {appeal.decided && (
            <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800 px-2 py-0.5 font-semibold text-gray-600 dark:text-gray-300">
              <Lock className="w-3 h-3" />
              Bağlanıb
            </span>
          )}
          {formatTime(appeal.createdAt)}
        </span>
      </div>
      <div className="mt-3">
        <AppealRequest appeal={appeal} />
      </div>
      <RichTextView html={appeal.text} className="mt-2" />

      {mine ? (
        <div
          className={cn(
            'mt-3 rounded-xl px-3 py-2.5 text-sm',
            mine.accepted
              ? 'bg-green-50 text-green-800 dark:bg-green-950 dark:text-green-200'
              : 'bg-red-50 text-red-800 dark:bg-red-950 dark:text-red-200'
          )}
        >
          <span className="inline-flex items-center gap-1.5 font-bold">
            {mine.accepted ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
            Sizin rəyiniz: {mine.accepted ? 'qəbul' : 'rədd'}
          </span>
          <p className="mt-1 whitespace-pre-line">{mine.text}</p>
        </div>
      ) : appeal.decided ? (
        <p className="mt-3 flex items-center gap-2 border-t border-gray-100 dark:border-gray-800 pt-3 text-sm text-gray-500 dark:text-gray-400">
          <Lock className="w-4 h-4 shrink-0" />
          {VERDICTS_TO_DECIDE} jüri artıq səs verib. Apelyasiya bağlanıb.
        </p>
      ) : (
        room.isOpen && <VerdictForm session={session} appeal={appeal} label={label} />
      )}
    </div>
  );
};

const AppealJuryView: React.FC<{ room: AppealRoom; session: AppealSession; onLeave: () => void }> = ({
  room,
  session,
  onLeave,
}) => {
  const [filter, setFilter] = useState<Filter>('todo');

  // Waiting for this juror: not voted yet and not already decided by three others
  const isTodo = (a: Appeal) => a.verdicts.length === 0 && !a.decided;
  const todoCount = room.appeals.filter(isTodo).length;
  const visible = room.appeals.filter(a => filter === 'all' || isTodo(a));
  const questions = room.questions.filter(q => visible.some(a => a.questionId === q.id));

  return (
    <div className="space-y-4">
      <RoomHeader room={room} role="jury" name={room.me} onLeave={onLeave} />

      {!room.isOpen && <ClosedNotice>Otaq bağlanıb. Rəy vermək mümkün deyil.</ClosedNotice>}

      <Tabs<Filter>
        value={filter}
        onChange={setFilter}
        tabs={[
          { key: 'todo', label: 'Rəy gözləyən', count: todoCount },
          { key: 'all', label: 'Bütün apelyasiyalar', count: room.appeals.length },
        ]}
      />

      {questions.length === 0 ? (
        <EmptyState icon={room.appeals.length === 0 ? Inbox : CheckCircle2}>
          {room.appeals.length === 0
            ? 'Hələ apelyasiya yoxdur. Komandalar apelyasiya verdikcə burada görünəcək.'
            : 'Rəy gözləyən apelyasiya yoxdur. Hamısını "Bütün apelyasiyalar" bölməsində görə bilərsiniz.'}
        </EmptyState>
      ) : (
        questions.map(q => {
          // Numbered within the question so jurors judge the argument, not the team
          const appeals = room.appeals.filter(a => a.questionId === q.id);
          return (
            <div key={q.id} className="grid gap-4 lg:grid-cols-2 lg:items-start">
              <div className={cn(cardCls, 'p-5 sm:p-6 lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto')}>
                <QuestionView question={q} />
              </div>
              <div className="space-y-3">
                {appeals.map((a, i) =>
                  filter === 'all' || isTodo(a) ? (
                    <AppealItem key={a.id} room={room} session={session} appeal={a} label={`Sual ${q.number} · apelyasiya ${i + 1}`} />
                  ) : null
                )}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default AppealJuryView;
